import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

/**
 * Token storage abstraction.
 *
 * "Remember me" controls persistence:
 *  - remember = true  → localStorage (survives browser restart)
 *  - remember = false → sessionStorage (cleared when the tab closes)
 *
 * Security note: web storage is readable by injected scripts (XSS). For higher
 * security, move to httpOnly cookies via SANCTUM_STATEFUL_DOMAINS + CSRF flow.
 */
@Injectable({ providedIn: 'root' })
export class TokenService {
  private readonly key = environment.tokenStorageKey;

  getToken(): string | null {
    return localStorage.getItem(this.key) ?? sessionStorage.getItem(this.key);
  }

  setToken(token: string, remember = false): void {
    const [target, other] = remember
      ? [localStorage, sessionStorage]
      : [sessionStorage, localStorage];
    other.removeItem(this.key); // never leave a stale copy in the other store
    target.setItem(this.key, token);
  }

  clearToken(): void {
    localStorage.removeItem(this.key);
    sessionStorage.removeItem(this.key);
  }

  hasToken(): boolean {
    return this.getToken() !== null;
  }

  /** True when the active token is persisted across sessions (remember me). */
  isPersistent(): boolean {
    return localStorage.getItem(this.key) !== null;
  }
}
